/**
 * Design tokens — primitive values only. Light/dark semantic mapping lives in
 * palette.ts; nothing outside src/theme should import a hex value from here.
 *
 * Contrast figures are WCAG ratios against the surface each colour sits on.
 */

export const light = {
  /** Brand primary as supplied (§4). 5.07:1 on white — AA for text and for white-on-red buttons. */
  primary: '#C53D49',
  primaryHover: '#A62F3C',
  onPrimary: '#FFFFFF',
  /** Brand secondary: black, used as ink and as the dark surface family. */
  ink: '#0B0B0C',
  inkMuted: '#57504F',
  inkDisabled: '#9A9191',
  background: '#FAF8F8',
  surface: '#FFFFFF',
  surfaceVariant: '#F2EFEF',
  divider: '#E5DFDF',
  success: '#2E6B4F',
  warning: '#8A5B0F',
  /** Deliberately not the brand red — an accent that also means "failed" reads as neither. */
  error: '#B3303C',
  info: '#2B5B8A',
} as const;

export const dark = {
  /** #C53D49 is only 4.14:1 on black, so dark mode uses a lighter red (~6.2:1). */
  primary: '#E4717C',
  primaryHover: '#F0929B',
  onPrimary: '#180E10',
  ink: '#F2EEEE',
  inkMuted: '#ADA3A2',
  inkDisabled: '#6E6566',
  background: '#101012',
  surface: '#1A1819',
  surfaceVariant: '#242122',
  divider: '#322E2F',
  success: '#6FC796',
  warning: '#DCA850',
  error: '#F08A93',
  info: '#83B6E2',
} as const;

/** WhatsApp green with dark text (9.9:1). White on this green fails AA. */
export const whatsapp = {
  main: '#25D366',
  hover: '#1EBE5A',
  onMain: '#0B0B0C',
} as const;

/** 4px base. */
export const radius = {
  sm: 4,
  md: 8,
  lg: 12,
  /**
   * Glass panels only: below this a frosted surface reads as a plain box, and
   * the rim's gradient has too little corner to turn on.
   */
  xl: 24,
  pill: 999,
} as const;

export const fontFamily = {
  display: '"Archivo Variable", "Helvetica Neue", Arial, sans-serif',
  body: '"Source Sans 3 Variable", "Segoe UI", Roboto, Arial, sans-serif',
} as const;

/** Minimum touch target (WCAG 2.5.5). */
export const touchTarget = 44;

/**
 * "This visitor has a real cursor."
 *
 * The one query every hover-only affordance hangs off, so the logo rows cannot
 * drift apart on what counts as a pointer device. Note it asks about the input
 * device, not the viewport: an iPad at 1024px has no hover and a 700px-wide
 * laptop window has one, so a width breakpoint gets both of those wrong.
 *
 * Where a label is only reachable by hovering — the client wall, Shop by Make —
 * the visible fallback is shown to everyone else by hiding it inside this
 * query, never by a `matchMedia` call: the site is prerendered at build time,
 * so a JS-decided label would mismatch the prerendered HTML or flash on load.
 */
export const POINTER_HOVER = '@media (hover: hover) and (pointer: fine)';
